import { apiFetch } from "@/auth/apiClient";

/** bKash payment status values */
export type BkashPaymentStatus =
  | "INITIATED"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  | "REFUNDED";

export type BkashPaymentRecord = {
  orderId: string;
  trxId: string | null;
  amount: string;
  currency: string;
  status: BkashPaymentStatus;
  productType: "COURSE" | "MOCK_TEST";
  productId: string;
  gateway: string;
  createdAt: string;
  paidAt: string | null;
};

/**
 * Initiate bKash payment for a mock test purchase.
 * Returns { bkashURL, paymentID, orderId } for paid tests
 * or { purchaseId, bkashURL: null } for free tests.
 */
export async function initMockTestBkashPayment(
  mockTestId: string,
  couponCode?: string,
  quantity = 1,
): Promise<{
  bkashURL: string | null;
  paymentID?: string;
  orderId?: string;
  purchaseId?: string;
}> {
  const json: { couponCode?: string; quantity?: number } = { quantity };
  if (couponCode) json.couponCode = couponCode;
  return apiFetch(`/lms/mock-tests/${mockTestId}/pay/bkash`, {
    method: "POST",
    json,
  });
}

/**
 * Initiate bKash payment for an all-access subscription plan.
 * Returns { bkashURL, ... } for paid plans, or { subscriptionId, bkashURL: null }
 * for free plans (activated immediately).
 */
export async function initSubscriptionBkashPayment(
  planId: string,
): Promise<{
  bkashURL: string | null;
  paymentID?: string;
  orderId?: string;
  subscriptionId?: string;
}> {
  return apiFetch(`/lms/mock-test-subscriptions/${planId}/pay/bkash`, {
    method: "POST",
    json: {},
  });
}

/**
 * Initiate bKash payment for a mock test bundle.
 * Returns { bkashURL, ... } for paid bundles, or { purchaseIds, bkashURL: null }
 * for free bundles (granted immediately).
 */
export async function initBundleBkashPayment(
  bundleId: string,
): Promise<{
  bkashURL: string | null;
  paymentID?: string;
  orderId?: string;
  purchaseIds?: string[];
}> {
  return apiFetch(`/lms/mock-test-bundles/${bundleId}/pay/bkash`, {
    method: "POST",
    json: {},
  });
}

/** Get my bKash payment history (student) */
export async function getMyBkashPayments(): Promise<BkashPaymentRecord[]> {
  return apiFetch<BkashPaymentRecord[]>("/lms/payments/my", { method: "GET" });
}

/** Query a specific payment status by orderId */
export async function getBkashPaymentStatus(orderId: string): Promise<{
  orderId: string;
  paymentId: string | null;
  trxId: string | null;
  status: BkashPaymentStatus;
  amount: string;
  currency: string;
  productType: "COURSE" | "MOCK_TEST";
  productId: string;
  createdAt: string;
  paidAt: string | null;
}> {
  return apiFetch(`/lms/payments/bkash/status/${encodeURIComponent(orderId)}`, {
    method: "GET",
  });
}

// ─── Admin ──────────────────────────────────────────────────────────────────

export type AdminPaymentRecord = BkashPaymentRecord & {
  id: string;
  paymentId: string | null;
  userId: number;
  originalAmount: string | null;
  discountAmount: string | null;
  couponCode: string | null;
  accessGranted: boolean;
  accessError: string | null;
  refundedAmount: string | null;
  refundedAt: string | null;
  refundedBy: number | null;
  refundReason: string | null;
  refundTrxId: string | null;
};

/** Admin: paginated list of all bKash payments */
export async function getAllBkashPayments(
  page = 1,
  limit = 50,
): Promise<{ items: AdminPaymentRecord[]; total: number; page: number; limit: number }> {
  return apiFetch(`/lms/payments/admin?page=${page}&limit=${limit}`, { method: "GET" });
}

/**
 * Signals the admin uses to check the published Refund Policy manually.
 * Nulls mean "not tracked — verify by hand"; they are not a pass.
 */
export type RefundEligibility = {
  orderId: string;
  status: BkashPaymentStatus;
  refundable: boolean;
  productType: "COURSE" | "MOCK_TEST";
  productId: string;
  userId: number;
  amountPaid: string;
  originalAmount: string | null;
  discountAmount: string | null;
  couponCode: string | null;
  trxId: string | null;
  paidAt: string | null;
  accessDelivered: boolean;
  signals: {
    daysSincePurchase: number | null;
    withinSevenDayWindow: boolean | null;
    isDuplicatePayment: boolean;
    courseProgressPercent: number | null;
    underTwentyPercentViewed: boolean | null;
    mockAttemptsUsed: number | null;
    mockPurchasedAttempts: number | null;
    mockUnattempted: boolean | null;
    certificateIssued: boolean | null;
    resourcesDownloaded: boolean | null;
    liveClassAttended: boolean | null;
  };
  note: string;
};

/** Admin: read-only refund decision panel data for one payment */
export async function getRefundEligibility(orderId: string): Promise<RefundEligibility> {
  return apiFetch(
    `/lms/payments/admin/${encodeURIComponent(orderId)}/refund-eligibility`,
    { method: "GET" },
  );
}

/**
 * Admin, step 1 of a refund: emails a one-time confirmation code to the acting
 * admin. Returns the masked address it was sent to.
 */
export async function requestRefundCode(orderId: string): Promise<{
  sentTo: string;
  expiresInMinutes: number;
}> {
  return apiFetch(
    `/lms/payments/admin/${encodeURIComponent(orderId)}/refund/request-code`,
    { method: "POST", json: {} },
  );
}

/**
 * Admin, step 2 of a refund: verifies the emailed code, then returns the money,
 * revokes access and releases any coupon.
 */
export async function refundBkashPayment(
  orderId: string,
  reason: string,
  confirmationCode: string,
): Promise<{
  success: boolean;
  orderId: string;
  status: BkashPaymentStatus;
  refundTrxId?: string | null;
  accessRevoked?: boolean;
  couponsReleased?: number;
  statusMessage?: string;
}> {
  return apiFetch(`/lms/payments/bkash/refund/${encodeURIComponent(orderId)}`, {
    method: "POST",
    json: { reason, confirmationCode },
  });
}

/** Admin: completed payments where product delivery failed */
export async function getPaymentsAwaitingAccess(): Promise<
  Array<{
    orderId: string;
    trxId: string | null;
    userId: number;
    amount: string;
    productType: "COURSE" | "MOCK_TEST";
    productId: string;
    accessError: string | null;
    paidAt: string | null;
    createdAt: string;
  }>
> {
  return apiFetch("/lms/payments/admin/awaiting-access", { method: "GET" });
}

/** Admin: retry access grants for paid-but-not-delivered orders */
export async function reconcilePaymentAccess(orderId?: string): Promise<{
  checked: number;
  granted: number;
  stillFailing: number;
}> {
  return apiFetch("/lms/payments/admin/reconcile-access", {
    method: "POST",
    json: orderId ? { orderId } : {},
  });
}
